import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Shield, Award, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Product, partners } from '../data/products';
import { images } from '../data/images';
import HeroCarousel from '../components/layout/HeroCarousel';
import ProductCard from '../components/ProductCard';
import { fetchAllProducts, ApiProduct } from '../services/api';
import './HomePage.css';

const HomePage: React.FC = () => {
    const { translations, isRTL } = useLanguage();
    const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Transform API product to local Product format
    const transformApiProduct = (apiProduct: ApiProduct): Product => {
        return {
            id: apiProduct.id,
            title: apiProduct.name.en,
            titleAr: apiProduct.name.ar,
            description: apiProduct.description.en,
            descriptionAr: apiProduct.description.ar,
            category: apiProduct.category,
            subcategory: apiProduct.subcategory,
            image: apiProduct.images[0] || '',
            images: apiProduct.images,
            price: apiProduct.price,
        };
    };

    // Fetch featured products from API
    useEffect(() => {
        const loadFeaturedProducts = async () => {
            try {
                setIsLoading(true);
                console.log('Fetching featured products from API...');
                const apiProducts = await fetchAllProducts();
                
                // Filter only active products and limit to 4
                const transformedProducts = apiProducts
                    .filter(p => p.isActive)
                    .map(transformApiProduct)
                    .slice(0, 4);
                
                console.log('Featured products loaded:', transformedProducts.length);
                setFeaturedProducts(transformedProducts);
            } catch (error) {
                console.error('Error loading featured products:', error);
                setFeaturedProducts([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadFeaturedProducts();
    }, []);

    return (
        <div className={`homepage ${isRTL ? 'rtl' : 'ltr'}`}>
            {/* Hero Carousel - Simple Image Slider */}
            <HeroCarousel />

            {/* Featured Products Section - Enhanced */}
            {!isLoading && featuredProducts.length > 0 && (
                <section className="featured-section">
                    <div className="container">
                        <div className="section-header">
                            <h3 className="section-title">{translations.featuredProducts}</h3>
                            <Link to="/products" className="section-link">
                                {translations.viewDetails}
                                <ArrowRight size={16} className={isRTL ? 'flip' : ''} />
                            </Link>
                        </div>

                        <div className="featured-products-grid">
                            {featuredProducts.map((product) => (
                                <div key={product.id} className="product-grid-item">
                                    <ProductCard product={product} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {isLoading && (
                <section className="featured-section">
                    <div className="container">
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                            <p>{translations.loading || 'Loading products...'}</p>
                        </div>
                    </div>
                </section>
            )}

            {/* About & Mission Section - Redesigned */}
            <section className="mission-section">
                <div className="container">
                    {/* Section Header */}
                    <div className="section-header">
                        {/* <span className="section-tag">{isRTL ? 'من نحن' : 'About Us'}</span> */}
                        <h2 className="section-title">{isRTL ? 'من نحن' : 'Who We Are'}</h2>
                        {/* <p className="section-subtitle">
                            {isRTL
                                ? 'تعرف على قصتنا ورؤيتنا في خدمة قطاع الرعاية الصحية'
                                : 'Learn about our story and vision in serving the healthcare sector'
                            }
                        </p> */}
                    </div>

                    {/* Main Content */}
                    <div className="mission-content">
                        <div className="mission-visual">
                            <div className="mission-image-wrapper">
                                <img src={images.about} alt="About Unicare Pharma" className="mission-main-image" />
                                <div className="mission-overlay">
                                    <div className="mission-stats-card">
                                        <div className="mission-stat">
                                            <div className="stat-icon">
                                                <Shield className="mission-icon" />
                                            </div>
                                            <div className="stat-content">
                                                <div className="mission-number">100%</div>
                                                <div className="mission-label">{isRTL ? 'جودة مضمونة' : 'Quality Assured'}</div>
                                            </div>
                                        </div>
                                        {/* <div className="mission-stat">
                                            <div className="stat-icon">
                                                <Award className="mission-icon" />
                                            </div>
                                            <div className="stat-content">
                                                <div className="mission-number">ISO</div>
                                                <div className="mission-label">{isRTL ? 'معايير دولية' : 'International Standards'}</div>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mission-text">
                            <div className="mission-intro">
                                <h3 className="intro-title">{isRTL ? 'قصتنا' : 'Our Story'}</h3>
                                <p className="intro-description">
                                    {isRTL
                                        ? 'دوائك عندنا هي شركة ناشئة سعودية متخصصة في تطوير وتسويق المنتجات الصيدلانية والتجميلية والاستهلاكية. نهدف إلى تزويد الأسواق المحلية والإقليمية بمنتجات مبتكرة وعالية الجودة تلبي المعايير العالمية وتطلعات المستهلكين.'
                                        : 'Dawaik Andna is a Saudi startup specializing in the development and marketing of pharmaceutical, cosmetic, and consumer products. We aim to provide the local and regional markets with innovative, high-quality products that meet global standards and consumer aspirations.'
                                    }
                                </p>
                            </div>

                            <div className="mission-features">
                                <div className="feature-item">
                                    <div className="feature-icon">
                                        <Shield size={24} />
                                    </div>
                                    <div className="feature-content">
                                        <h4>{isRTL ? 'منتجات آمنة ومعتمدة' : 'Safe & Certified Products'}</h4>
                                        <p>{isRTL ? 'جميع منتجاتنا معتمدة وفقاً لأعلى المعايير العالمية' : 'All our products are certified according to the highest global standards'}</p>
                                    </div>
                                </div>
                                <div className="feature-item">
                                    <div className="feature-icon">
                                        <Heart size={24} />
                                    </div>
                                    <div className="feature-content">
                                        <h4>{isRTL ? 'رعاية شخصية متخصصة' : 'Personalized Care'}</h4>
                                        <p>{isRTL ? 'نقدم حلول مخصصة تناسب احتياجات كل عميل' : 'We provide customized solutions that meet each client\'s needs'}</p>
                                    </div>
                                </div>
                                {/* <div className="feature-item">
                                    <div className="feature-icon">
                                        <Users size={24} />
                                    </div>
                                    <div className="feature-content">
                                        <h4>{isRTL ? 'فريق من الخبراء' : 'Expert Team'}</h4>
                                        <p>{isRTL ? 'فريق متخصص من الخبراء في مجال الرعاية الصحية' : 'Specialized team of experts in healthcare'}</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Vision & Mission Cards */}
                    <div className="vision-mission-cards">
                        <div className="vision-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <Award size={32} />
                                </div>
                                <h3 className="card-title">{isRTL ? 'رؤيتنا' : 'Our Vision'}</h3>
                            </div>
                            <p className="card-description">
                                {isRTL
                                    ? 'أن نصبح شريكاً موثوقاً في قطاع الرعاية الصحية والجمال، من خلال تقديم منتجات تجمع بين الجودة والفعالية والابتكار.'
                                    : 'To become a trusted partner in the healthcare and beauty sector, by offering products that combine quality, effectiveness, and innovation.'
                                }
                            </p>
                        </div>

                        <div className="mission-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <Heart size={32} />
                                </div>
                                <h3 className="card-title">{isRTL ? 'مهمتنا' : 'Our Mission'}</h3>
                            </div>
                            <p className="card-description">
                                {isRTL
                                    ? 'نحن ملتزمون بتوفير منتجات آمنة وفعالة تساهم في تحسين جودة حياة الناس وتدعم النمو المستدام لقطاع الصحة والجمال في المنطقة.'
                                    : 'We are committed to providing safe and effective products that contribute to improving people\'s quality of life and support the sustainable growth of the health and beauty sector in the region.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Brand Showcase - New CareToBeauty Style */}
            {/* <BrandShowcase /> */}

            {/* Contact Section - Simple Design */}
            <section className="contact-section">
                <div className="container">
                    {/* Section Header */}
                    <div className="section-header">
                        <h2 className="section-title">{isRTL ? 'كيف يمكننا مساعدتك؟' : 'How Can We Help You?'}</h2>                    
                    </div>
                    <div className="contact-content">
                        <div className="contact-info">
                            <h3 className="contact-title">{isRTL ? 'تواصل معنا' : 'Contact Us'}</h3>
                            <p className="section-subtitle">
                            {isRTL
                                ? 'فريقنا المتخصص متاح لخدمتكم على مدار الساعة لتقديم الدعم والمشورة المهنية'
                                : 'Our specialized team is available 24/7 to provide support and professional consultation'
                            }
                        </p>
                        </div>
                        <div className="contact-action">
                            <Link to="/contact" className="contact-btn">
                                <Phone size={20} />
                                <span>{translations.contactUs}</span>
                                <ArrowRight size={16} className={isRTL ? 'flip' : ''} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="partners-section">
                <div className="container">
                    <h2>{translations.ourPartners}</h2>
                    <div className="partners-banner">
                        <div className="partners-scroll">
                            {partners.map(partner => (
                                <div key={partner.id} className="partner-logo">
                                    <img src={partner.logo} alt={partner.name} />
                                </div>
                            ))}
                            {/* Duplicate for seamless scroll */}
                            {partners.map(partner => (
                                <div key={`${partner.id}-duplicate`} className="partner-logo">
                                    <img src={partner.logo} alt={partner.name} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default HomePage;